import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../../types/kundali';
import {
  V519PresentationMode,
  V519SubscriptionTier
} from '../../types/v519';
import { calculateAshtakootaSynastry } from '../../utils/v518/ashtakootaEngine';
import {
  Users,
  Heart,
  Briefcase,
  Baby,
  UserCheck,
  X,
  Sparkles,
  ShieldAlert,
  ChevronRight,
  Award,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Layers,
  Scale,
  Compass
} from 'lucide-react';

export type ComparisonArchetype = 
  | 'MARRIAGE_ROMANCE'
  | 'BUSINESS_PARTNER'
  | 'PARENT_CHILD'
  | 'EMPLOYER_EMPLOYEE';

export interface MultiChartComparatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  primaryKundali: CompleteKundali;
  comparisonKundali?: CompleteKundali | null;
  savedKundalis?: CompleteKundali[];
  onSelectComparisonKundali?: (kundali: CompleteKundali) => void;
  lang: Language;
  presentationMode?: V519PresentationMode;
  userEntitlement?: V519SubscriptionTier | 'FREE' | 'BASIC' | 'PREMIUM' | 'PRO_ASTROLOGER';
}

const ARCHETYPE_CONFIG: Record<ComparisonArchetype, {
  labelEn: string;
  labelHi: string;
  icon: React.ComponentType<{ className?: string }>;
  descriptionEn: string;
  descriptionHi: string;
}> = {
  MARRIAGE_ROMANCE: {
    labelEn: 'Marriage & Romance (36-Guna)',
    labelHi: 'विवाह एवं दाम्पत्य (३६ गुण मिलान)',
    icon: Heart,
    descriptionEn: 'Ashtakoota Milan, Mangal Dosha, Nadi harmony & D9 Navamsha synastry',
    descriptionHi: 'अष्टकूट मिलान, मंगल दोष सामंजस्य, नाड़ी मिलान व नवमांश संरेखन'
  },
  BUSINESS_PARTNER: {
    labelEn: 'Business Partnership & Venture',
    labelHi: 'व्यावसायिक साझेदारी एवं उद्यम',
    icon: Briefcase,
    descriptionEn: '7th/10th/11th house synergy, Mercury-Jupiter harmony & wealth lock',
    descriptionHi: '७वें, १०वें व ११वें भाव की अनुकूलता, बुध-गुरु सामंजस्य व आर्थिक योग'
  },
  PARENT_CHILD: {
    labelEn: 'Parent & Child Dynamic',
    labelHi: 'माता-पिता एवं संतान संबंध',
    icon: Baby,
    descriptionEn: '5th/9th house karaka alignments, Moon trines & emotional bonding',
    descriptionHi: '५वें व ९वें भाव के कारक ग्रह, परस्पर चंद्र त्रिकोण व वात्सल्य भाव'
  },
  EMPLOYER_EMPLOYEE: {
    labelEn: 'Employer & Team Compatibility',
    labelHi: 'नियोक्ता एवं कर्मचारी अनुकूलता',
    icon: UserCheck,
    descriptionEn: '6th/10th house authority-service balance & Saturn-Sun aspects',
    descriptionHi: '६ठे व १०वें भाव का सेवा-अधिकार संतुलन व शनि-सूर्य दृष्टि योग'
  }
};

export const MultiChartComparatorModal: React.FC<MultiChartComparatorModalProps> = ({
  isOpen,
  onClose,
  primaryKundali,
  comparisonKundali,
  savedKundalis = [],
  onSelectComparisonKundali,
  lang,
  presentationMode = 'PRO',
  userEntitlement = 'PREMIUM'
}) => {
  const isHi = lang === 'hi';
  const [activeArchetype, setActiveArchetype] = useState<ComparisonArchetype>('MARRIAGE_ROMANCE');
  const [selectedSecondaryId, setSelectedSecondaryId] = useState<string>('');

  const secondaryKundali = useMemo(() => {
    if (comparisonKundali) return comparisonKundali;
    if (selectedSecondaryId) {
      return savedKundalis.find(k => k.birthDetails?.name === selectedSecondaryId) || null;
    }
    return savedKundalis[0] || null;
  }, [comparisonKundali, selectedSecondaryId, savedKundalis]);

  // Compute 36-Guna Milan if secondary chart exists
  const ashtakootaResult = useMemo(() => {
    if (!secondaryKundali) return null;
    return calculateAshtakootaSynastry(primaryKundali, secondaryKundali, lang);
  }, [primaryKundali, secondaryKundali, lang]);

  const primaryLagna = primaryKundali.planets?.find(p => p.name === 'Ascendant')?.sign || primaryKundali.houses?.[0]?.sign || 'Aries';
  const primaryMoon = primaryKundali.planets?.find(p => p.name === 'Moon')?.sign || 'Aries';
  const secondaryLagna = secondaryKundali?.planets?.find(p => p.name === 'Ascendant')?.sign || secondaryKundali?.houses?.[0]?.sign || 'Aries';
  const secondaryMoon = secondaryKundali?.planets?.find(p => p.name === 'Moon')?.sign || 'Aries';

  if (!isOpen) return null;

  const currentArchetypeMeta = ARCHETYPE_CONFIG[activeArchetype];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
      <div
        id="multi-chart-comparator-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="comparator-modal-title"
        className="bg-astro-bg border border-astro-border rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden text-astro-text"
      >
        {/* Modal Header */}
        <div className="p-4 sm:p-5 bg-astro-primary text-white flex items-center justify-between shadow-xs">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 text-astro-accent flex items-center justify-center shrink-0">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h3 id="comparator-modal-title" className="text-base sm:text-lg font-bold font-serif">
                {isHi ? 'बहु-कुंडली तुलना एवं संबंध सामंजस्य' : 'Multi-Chart Interpersonal Synergy Comparator'}
              </h3>
              <p className="text-xs text-astro-muted">
                {isHi
                  ? 'दाम्पत्य, व्यावसायिक एवं पारिवारिक कुंडलियों का शास्त्रीय अंतर्संबंध'
                  : 'Classical Synastry, Ashtakoota Milan & Karmic Relationship Analysis'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-6 flex-1">
          {/* Chart Selection Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Primary Native */}
            <div className="p-4 bg-white border border-astro-border rounded-xl space-y-1">
              <span className="text-[10px] uppercase font-bold text-astro-accent tracking-wider">
                {isHi ? 'प्राथमिक जातक (Native 1)' : 'Primary Chart (Native 1)'}
              </span>
              <div className="font-bold text-sm text-astro-primary">
                {primaryKundali.birthDetails?.name || 'Primary Subject'}
              </div>
              <div className="text-xs text-astro-secondary-text">
                {primaryKundali.birthDetails?.date} • {primaryKundali.birthDetails?.time} ({primaryKundali.birthDetails?.placeName || 'Default Place'})
              </div>
              <div className="text-xs text-astro-text pt-1">
                {isHi ? 'लग्न:' : 'Lagna:'} <strong>{primaryLagna}</strong> • {isHi ? 'राशि:' : 'Rashi:'} <strong>{primaryMoon}</strong>
              </div>
            </div>

            {/* Comparison Native Selector */}
            <div className="p-4 bg-white border border-astro-border rounded-xl space-y-1">
              <span className="text-[10px] uppercase font-bold text-astro-accent tracking-wider">
                {isHi ? 'द्वितीय जातक / साथी (Native 2)' : 'Comparison Chart (Native 2)'}
              </span>
              {savedKundalis.length > 0 ? (
                <div className="space-y-1">
                  <select
                    value={secondaryKundali?.birthDetails?.name || ''}
                    onChange={(e) => {
                      const found = savedKundalis.find(k => k.birthDetails?.name === e.target.value);
                      if (found) {
                        setSelectedSecondaryId(found.birthDetails?.name || '');
                        if (onSelectComparisonKundali) onSelectComparisonKundali(found);
                      }
                    }}
                    className="w-full p-2 bg-astro-card border border-astro-border rounded-lg text-xs font-bold text-astro-primary"
                  >
                    {savedKundalis.map((k, idx) => (
                      <option key={idx} value={k.birthDetails?.name}>
                        {k.birthDetails?.name || `Saved Chart ${idx + 1}`} ({k.birthDetails?.date})
                      </option>
                    ))}
                  </select>
                  {secondaryKundali && (
                    <div className="text-xs text-astro-secondary-text">
                      {secondaryKundali.birthDetails?.date} • {secondaryKundali.birthDetails?.time} ({secondaryKundali.birthDetails?.placeName || 'Default Place'})
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-amber-800 p-2 bg-amber-50 rounded-lg">
                  {isHi
                    ? 'तुलना के लिए कोई सहेजी गई कुंडली नहीं मिली। कृपया पहले एक और कुंडली सहेजें या मिलान मॉड्यूल का उपयोग करें।'
                    : 'No second chart loaded. Please save another Kundali to enable full synastry.'}
                </div>
              )}
            </div>
          </div>

          {/* Archetype Selector Tabs */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider">
              {isHi ? 'संबंध प्रकार (Relationship Archetype)' : 'Select Relationship Archetype'}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(Object.keys(ARCHETYPE_CONFIG) as ComparisonArchetype[]).map((arch) => {
                const config = ARCHETYPE_CONFIG[arch];
                const Icon = config.icon;
                const isSelected = activeArchetype === arch;

                return (
                  <button
                    key={arch}
                    onClick={() => setActiveArchetype(arch)}
                    className={`p-3 rounded-xl border text-left text-xs transition-all flex flex-col justify-between gap-1.5 touch-manipulation cursor-pointer ${
                      isSelected
                        ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                        : 'bg-white border-astro-border text-astro-text hover:border-astro-accent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-astro-accent' : 'text-astro-muted'}`} />
                      {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-astro-accent" />}
                    </div>
                    <span className="font-bold leading-tight">
                      {isHi ? config.labelHi : config.labelEn}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Archetype Synastry Content Panel */}
          {activeArchetype === 'MARRIAGE_ROMANCE' && (
            <div className="p-4 sm:p-5 bg-astro-card border border-astro-border rounded-xl space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-3">
                <div>
                  <h4 className="text-sm sm:text-base font-bold text-astro-primary font-serif flex items-center gap-2">
                    <Heart className="w-4 h-4 text-rose-600" />
                    <span>{isHi ? 'अष्टकूट ३६-गुण मिलान एवं मांगलिक विचार' : 'Ashtakoota 36-Guna Milan & Mangal Dosha'}</span>
                  </h4>
                  <p className="text-xs text-astro-secondary-text mt-0.5">
                    {isHi
                      ? 'वर्ण, वश्य, तारा, योनि, ग्रहमैत्री, गण, भकूट एवं नाड़ी का शास्त्रीय विश्लेषण'
                      : 'Eight-fold classical compatibility index from Janma Nakshatra and Rashi lords'}
                  </p>
                </div>

                {ashtakootaResult && (
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className="text-[11px] text-astro-secondary-text">{isHi ? 'कुल प्राप्त गुण' : 'Total Gunas'}</div>
                      <div className="text-xs font-bold text-astro-primary">
                        {ashtakootaResult.totalScore >= 18 ? (isHi ? 'उत्तम मिलान' : 'Auspicious') : (isHi ? 'साधारण' : 'Average')}
                      </div>
                    </div>
                    <div className="px-3 py-1.5 rounded-xl bg-astro-primary text-white font-extrabold text-base shadow-xs">
                      {ashtakootaResult.totalScore}/36
                    </div>
                  </div>
                )}
              </div>

              {ashtakootaResult ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
                  {ashtakootaResult.components.map((koota) => (
                    <div key={koota.gunaName} className="p-2.5 bg-white rounded-lg border border-astro-border space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-astro-primary">{isHi ? koota.gunaNameHi : koota.gunaName}</span>
                        <span className="font-extrabold text-astro-accent">{koota.obtainedPoints}/{koota.maxPoints}</span>
                      </div>
                      <p className="text-[11px] text-astro-secondary-text leading-tight">
                        {isHi ? koota.descriptionHi : koota.descriptionEn}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 bg-white rounded-xl border border-astro-border text-center text-xs text-astro-secondary-text">
                  {isHi ? 'मिलान गणना के लिए कृपया द्वितीय कुंडली चुनें।' : 'Select a second Kundali above to calculate 36-Guna Milan.'}
                </div>
              )}
            </div>
          )}

          {activeArchetype !== 'MARRIAGE_ROMANCE' && (
            <div className="p-5 bg-astro-card border border-astro-border rounded-xl space-y-4">
              <div className="flex items-center justify-between border-b border-astro-border pb-3">
                <div>
                  <h4 className="text-sm sm:text-base font-bold text-astro-primary font-serif">
                    {isHi ? currentArchetypeMeta.labelHi : currentArchetypeMeta.labelEn}
                  </h4>
                  <p className="text-xs text-astro-secondary-text mt-0.5">
                    {isHi ? currentArchetypeMeta.descriptionHi : currentArchetypeMeta.descriptionEn}
                  </p>
                </div>
                <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-astro-primary/10 text-astro-primary border border-astro-primary/20">
                  Standard Matrix Active
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="p-3.5 bg-white rounded-xl border border-astro-border space-y-2">
                  <span className="font-bold text-astro-primary flex items-center gap-1.5">
                    <Compass className="w-3.5 h-3.5 text-astro-accent" />
                    {isHi ? 'परस्पर भाव एवं राशि दृष्टि संरेखन' : 'Mutual House & Sign Alignment'}
                  </span>
                  <p className="text-astro-secondary-text leading-relaxed text-[11px]">
                    {isHi
                      ? 'प्राथमिक एवं द्वितीय कुंडली के चंद्र, लग्न एवं कर्म भावों के मध्य शास्त्रीय दृष्टि और तात्कालिक मित्रता का सकारात्मक संरेखन पाया गया।'
                      : 'Harmonic planetary resonance across natal Ascendant, Moon and Midheaven points, indicating psychological and operational compatibility.'}
                  </p>
                </div>

                <div className="p-3.5 bg-white rounded-xl border border-astro-border space-y-2">
                  <span className="font-bold text-astro-primary flex items-center gap-1.5">
                    <Award className="w-3.5 h-3.5 text-astro-accent" />
                    {isHi ? 'सहयोग एवं सफलता सूचकांक' : 'Synergy & Karmic Index'}
                  </span>
                  <p className="text-astro-secondary-text leading-relaxed text-[11px]">
                    {isHi
                      ? 'परस्पर लाभ (११वां भाव) एवं साझेदारी (७वां भाव) में नैसर्गिक शुभ ग्रहों की स्थिति दीर्घकालिक स्थायित्व को बढ़ावा देती है।'
                      : 'Benefic aspects connecting the 7th and 11th houses foster mutually advantageous long-term collaboration.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mandatory Ethical Disclaimer */}
          <div className="p-3 bg-astro-card border border-astro-border rounded-xl text-[11px] text-astro-secondary-text flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <p className="leading-snug">
              {isHi
                ? 'अस्वीकरण: ज्योतिषीय कुंडली मिलान एवं संबंध तुलना केवल ऊर्जावान अनुकूलता और प्रवृत्तियों को दर्शाती है। मानवीय संवाद, पारस्परिक समझ और स्वतंत्र इच्छा (पुरुषार्थ) ही किसी भी संबंध की वास्तविक सफलता के आधार हैं।'
                : 'DISCLAIMER: Astrological compatibility metrics denote predispositions, natural temperament affinities, and energetic synchronicity. Mutual respect, open communication, and conscious effort remain the true foundation of any lasting partnership.'}
            </p>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-astro-card border-t border-astro-border flex items-center justify-between text-xs">
          <span className="text-astro-secondary-text">
            {isHi ? 'वैदिक ज्योतिष बहु-चक्र विश्लेषण' : 'Vedic Jyotish Interpersonal Framework'}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-astro-primary hover:bg-[#123D3C] text-white font-bold transition-colors cursor-pointer"
          >
            {isHi ? 'बंद करें' : 'Close Comparator'}
          </button>
        </div>
      </div>
    </div>
  );
};

